import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { commissionService, getErrorMessage } from '@/services';
import { formatCalcRunSummary } from '@/pages/commissions/calcRunSummary';
import type { ReconciliationFilters } from '@/services/commissions';

export function useRecalculateCommission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (policy: string) => commissionService.recalculateCommission(policy),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['policy'] });
      qc.invalidateQueries({ queryKey: ['commissionCalculations'] });
      // W0-A (B3): no success toast. A blanket "Commission recalculated" is
      // false when the result was unchanged (nothing posted) or when a paid
      // commission was corrected by a clawback/adjustment. The caller renders
      // `describeRecalcOutcome(result)` (PolicyDetailPage's inline notice, its
      // only consumer) -- one message, and a true one.
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

export function useCommissionCalculations(page = 1, filters?: Record<string, string>) {
  return useQuery({
    queryKey: ['commissionCalculations', page, filters],
    queryFn: () => commissionService.getCalculations(page, filters),
    placeholderData: (prev) => prev,
  });
}

export function useCommissionPayouts(page = 1, filters?: Record<string, string>) {
  return useQuery({
    queryKey: ['commissionPayouts', page, filters],
    queryFn: () => commissionService.getPayouts(page, filters),
    placeholderData: (prev) => prev,
  });
}

export function useCommissionLedger(page = 1, filters?: Record<string, string>) {
  return useQuery({
    queryKey: ['commissionLedger', page, filters],
    queryFn: () => commissionService.getLedger(page, filters),
    placeholderData: (prev) => prev,
  });
}

/** #692: the POSP ledger statement for one financial year. */
export function usePospStatement(pospId: string | undefined, financialYear: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['pospStatement', pospId, financialYear],
    queryFn: () => commissionService.getPospStatement({ posp_id: pospId, financial_year: financialYear }),
    enabled,
    placeholderData: (prev) => prev,
  });
}

export function useRunningBalance(brokerId: string | undefined) {
  return useQuery({
    queryKey: ['runningBalance', brokerId],
    queryFn: () => commissionService.getRunningBalance(brokerId!),
    enabled: !!brokerId,
  });
}

/** What ONE AGENT is still owed. See `getPospRunningBalance` for why this is a
 *  separate hook rather than an argument to the broker one. */
export function usePospRunningBalance(pospId: string | undefined) {
  return useQuery({
    queryKey: ['pospRunningBalance', pospId],
    queryFn: () => commissionService.getPospRunningBalance(pospId!),
    enabled: !!pospId,
  });
}

export function useBrokerRates(page = 1, filters?: Record<string, string>) {
  return useQuery({
    queryKey: ['brokerRates', page, filters],
    queryFn: () => commissionService.getBrokerRates(page, filters),
    placeholderData: (prev) => prev,
  });
}

export function useCreateBrokerRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof commissionService.createBrokerRate>[0]) =>
      commissionService.createBrokerRate(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['brokerRates'] });
      toast.success('Commission rate added');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

export function useUpdateBrokerRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof commissionService.updateBrokerRate>[1] }) =>
      commissionService.updateBrokerRate(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['brokerRates'] });
      toast.success('Commission rate updated');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

export function useDeleteBrokerRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => commissionService.deleteBrokerRate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['brokerRates'] });
      toast.success('Commission rate removed');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

export function useInsurerInsuranceTypes(insurerId?: string) {
  return useQuery({
    queryKey: ['insurerInsuranceTypes', insurerId],
    queryFn: () => commissionService.getInsurerInsuranceTypes(insurerId!),
    enabled: !!insurerId,
  });
}

export function useUploadBrokerRatesCsv() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, insurerId }: { file: File; insurerId?: string }) =>
      commissionService.uploadBrokerRatesCsv(file, insurerId),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['brokerRates'] });
      if (data.created > 0) toast.success(`${data.created} rate(s) imported`);
      if (data.error_count > 0) toast.error(`${data.error_count} row(s) failed — see preview`);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

export function useCommissionSlabs(page = 1, filters?: Record<string, string>) {
  return useQuery({
    queryKey: ['commissionSlabs', page, filters],
    queryFn: () => commissionService.getSlabs(page, filters),
    placeholderData: (prev) => prev,
  });
}

export function useCreateSlab() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof commissionService.createSlab>[0]) =>
      commissionService.createSlab(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commissionSlabs'] });
      toast.success('Slab added');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

export function useUpdateSlab() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof commissionService.updateSlab>[1] }) =>
      commissionService.updateSlab(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commissionSlabs'] });
      toast.success('Slab updated');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

export function useDeleteSlab() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => commissionService.deleteSlab(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commissionSlabs'] });
      toast.success('Slab removed');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

export function useCommissionWaterfalls(filters?: Record<string, string>) {
  return useQuery({
    queryKey: ['commissionWaterfalls', filters],
    queryFn: () => commissionService.getWaterfalls(filters),
  });
}

/**
 * Queue #200/#221: starts the background run. The mutation resolves as soon as
 * the run exists, NOT when the batch finishes -- `useCommissionCalcRun` below
 * follows it to completion. There is deliberately no success toast here: the
 * work has not happened yet, and claiming otherwise is the failure mode this
 * whole change exists to remove.
 */
export function useCalculateCommissions() {
  return useMutation({
    mutationFn: ({ fromDate, toDate }: { fromDate?: string; toDate?: string }) =>
      commissionService.calculateCommissions(fromDate, toDate),
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

/**
 * Polls a run until it finishes, then announces the outcome once.
 *
 * `runId` null means "not watching anything". Polling stops the moment the run
 * reaches a terminal state, so a finished run is not re-fetched every two
 * seconds for as long as the page stays open.
 */
export function useCommissionCalcRun(runId: string | null) {
  const qc = useQueryClient();
  const announced = useRef<string | null>(null);

  const query = useQuery({
    queryKey: ['commissionCalcRun', runId],
    queryFn: () => commissionService.getCalculationRun(runId ?? undefined),
    enabled: Boolean(runId),
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      return status === 'completed' || status === 'failed' ? false : 2000;
    },
  });

  const run = query.data;
  useEffect(() => {
    if (!run || announced.current === run.id) return;
    if (run.status === 'completed') {
      announced.current = run.id;
      qc.invalidateQueries({ queryKey: ['commissionCalculations'] });
      qc.invalidateQueries({ queryKey: ['commissionLedger'] });
      toast.success(formatCalcRunSummary(run));
    } else if (run.status === 'failed') {
      announced.current = run.id;
      toast.error(run.error_message || 'The commission calculation failed.');
    }
  }, [run, qc]);

  return query;
}

export function useApproveCalculation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => commissionService.approveCalculation(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['commissionCalculations'] }); toast.success('Calculation approved'); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

export function useDisputeCalculation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => commissionService.disputeCalculation(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['commissionCalculations'] }); toast.success('Calculation disputed'); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

export function useCreatePayoutBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { entity_type: string; entity_id: string; start_date: string; end_date: string }) =>
      commissionService.createPayoutBatch(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commissionPayouts'] });
      qc.invalidateQueries({ queryKey: ['pospOutstanding'] });
      toast.success('Payout batch created');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

export function useMarkPayoutProcessing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => commissionService.markProcessing(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['commissionPayouts'] }); toast.success('Payout processing'); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

export function useMarkPayoutCompleted() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      paymentReference,
      paymentDate,
      paymentMode,
    }: {
      id: string;
      paymentReference: string;
      paymentDate?: string;
      paymentMode?: string;
    }) => commissionService.markCompleted(id, { paymentReference, paymentDate, paymentMode }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commissionPayouts'] });
      qc.invalidateQueries({ queryKey: ['runningBalance'] });
      qc.invalidateQueries({ queryKey: ['commissionLedger'] });
      qc.invalidateQueries({ queryKey: ['pospOutstanding'] });
      toast.success('Payout completed');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

/** B13: the policies inside one payout. Idle until a payout is chosen. */
export function usePayoutLines(payoutId: string | null | undefined) {
  return useQuery({
    queryKey: ['commissionPayoutLines', payoutId],
    queryFn: () => commissionService.getPayoutLines(payoutId!),
    enabled: !!payoutId,
  });
}

/**
 * B12: reconciliation lines with the tab's filters. The key starts with
 * `statementLines` so `useUploadStatement`'s invalidation still refreshes it.
 */
export function useReconciliationLines(page = 1, filters?: ReconciliationFilters) {
  return useQuery({
    queryKey: ['statementLines', 'filtered', page, filters],
    queryFn: () => commissionService.getReconciliationLines(page, filters),
    placeholderData: (prev) => prev,
  });
}

/** B12: the summary for the SAME filters as the list (it used to ignore them). */
export function useFilteredReconciliationSummary(filters?: ReconciliationFilters) {
  return useQuery({
    queryKey: ['reconciliationSummary', 'filtered', filters],
    queryFn: () => commissionService.getReconciliationSummary(filters),
  });
}
